import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth, useTheme } from "../App";
import { 
  Moon,
  Sun,
  Menu,
  X,
  LogOut,
  User,
  Settings,
  Home
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Header() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll for header shrink effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Navigation items
  const navItems = [
    { label: "Dictionnaire", path: "/dictionary" },
    { label: "Apprendre", path: "/learn" },
    { label: "Quiz", path: "/quiz" },
    { label: "Tendances", path: "/trends" },
    { label: "Communauté", path: "/community" },
  ];

  // Check if current path matches nav item
  const isActive = (path) => location.pathname === path;

  return (
    <header className={`header-main transition-all duration-300 ${isScrolled ? 'py-1 shadow-md' : ''}`}>
      <div className={`max-w-7xl mx-auto px-4 flex justify-between items-center transition-all duration-300 ${isScrolled ? 'py-1' : 'py-3'}`}>
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="site-logo">
          <img 
            src={`${BACKEND_URL}/api/images/logo_mon_baoule.png`} 
            alt="Mon Baoulé" 
            className={`w-auto object-contain transition-all duration-300 ${isScrolled ? 'h-10 sm:h-12' : 'h-16 sm:h-20 lg:h-24'}`}
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <Link 
              key={item.path}
              to={item.path} 
              className={`nav-link transition-colors ${
                isActive(item.path) 
                  ? 'text-primary font-semibold' 
                  : 'text-foreground hover:text-primary'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Right Side Actions */}
        <div className="flex items-center gap-3">
          {/* Theme Toggle */}
          <button 
            onClick={toggleTheme} 
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? (
              <Sun className="w-5 h-5 text-primary" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
          </button>
          
          {/* User Actions */}
          {user ? (
            <>
              {/* Dashboard Button */}
              <Button 
                onClick={() => navigate('/dashboard')} 
                className={`rounded-full hidden sm:flex transition-all duration-300 ${
                  isScrolled ? 'h-8 text-sm' : ''
                } ${isActive('/dashboard') ? 'bg-primary/90 hover:bg-primary' : ''}`}
              >
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </Button>

              {/* Profile Picture / Avatar */}
              <Link 
                to="/profile" 
                className="hidden sm:flex items-center gap-2 hover:opacity-80 transition-opacity"
                title="Mon profil"
              >
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-9 h-9 rounded-full border-2 border-primary" 
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full gradient-wax flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                )}
              </Link>

              {/* Admin Link (if admin) */}
              {user.role === 'admin' && (
                <Link 
                  to="/admin/cms"
                  className="hidden md:flex p-2 rounded-lg hover:bg-muted transition-colors"
                  title="Admin CMS"
                >
                  <Settings className="w-5 h-5" />
                </Link>
              )}

              {/* Logout Button */}
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleLogout} 
                className="rounded-lg hidden sm:flex"
                aria-label="Déconnexion"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </>
          ) : (
            <Button 
              onClick={() => navigate('/register')} 
              className={`rounded-full hidden sm:flex transition-all duration-300 ${
                isScrolled ? 'h-8 text-sm' : ''
              }`}
            >
              Commencer
            </Button>
          )}
          
          {/* Mobile Menu Toggle */}
          <button 
            className="lg:hidden p-2" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-background border-t border-border py-4 px-4 animate-slide-up">
          <div className="flex flex-col gap-3">
            {navItems.map((item) => (
              <Link 
                key={item.path}
                to={item.path} 
                className={`py-2 font-semibold transition-colors ${
                  isActive(item.path) ? 'text-primary' : 'text-foreground'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            
            {user ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="py-2 font-semibold"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                
                <Link 
                  to="/profile" 
                  className="py-2 font-semibold"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Mon Profil
                </Link>
                
                {user.role === 'admin' && (
                  <Link 
                    to="/admin/cms" 
                    className="py-2 font-semibold text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin CMS
                  </Link>
                )}
                
                <Button 
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }} 
                  variant="outline"
                  className="rounded-full mt-2 w-full"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </Button>
              </>
            ) : (
              <Button 
                onClick={() => {
                  navigate('/register');
                  setMobileMenuOpen(false);
                }} 
                className="rounded-full mt-2"
              >
                Commencer
              </Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
